import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Filters.module.scss";
import Select from "../../../components/common/select/Select";
import Button from "../../../components/common/button/Button";
import DatePicker from "../../../components/common/date-picker/DatePicker";
import { StoreTypes } from "../../../api/types/products.types";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import StoresService from "../../../api/services/stores.service";
import Input from "../../../components/common/input/Input";

interface FiltersProps {
  storeId: string;
  startDate: string;
  endDate: string;
  onChangeStore: (storeId: string, storeName?: string) => void;
  onChangeStartDate: (date: string) => void;
  onChangeEndDate: (date: string) => void;
  onApply: () => void;
  onReset: () => void;
}

const formatDate = (date: Date) => date.toISOString().slice(0, 10);

const Filters = ({
  storeId,
  startDate,
  endDate,
  onChangeStore,
  onChangeStartDate,
  onChangeEndDate,
  onApply,
  onReset,
}: FiltersProps) => {
  const { t } = useTranslation("translation");
  const [storeSearch, setStoreSearch] = useState("");
  const { data: stores, loaderRef } = useInfiniteScroll<StoreTypes>({
    fetchFn: async (page) => {
      const res = await StoresService.getALLStores(
        page,
        20,
        storeSearch || undefined,
      );
      return res.data.data;
    },
    pageSize: 20,
    deps: [storeSearch],
  });

  const setRange = (from: Date, to: Date) => {
    onChangeStartDate(formatDate(from));
    onChangeEndDate(formatDate(to));
  };

  const quickRanges = [
    {
      label: t("filters.quick_ranges.today") as string,
      action: () => {
        const today = new Date();
        setRange(today, today);
      },
    },
    {
      label: t("filters.quick_ranges.yesterday") as string,
      action: () => {
        const y = new Date();
        y.setDate(y.getDate() - 1);
        setRange(y, y);
      },
    },
    {
      label: t("filters.quick_ranges.last_7_days") as string,
      action: () => {
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - 6);
        setRange(from, to);
      },
    },
    {
      label: t("filters.quick_ranges.this_week") as string,
      action: () => {
        const to = new Date();
        const day = to.getDay(); // 0 (Sun) - 6 (Sat)
        const diffToMonday = day === 0 ? 6 : day - 1; // make Monday start
        const from = new Date(to);
        from.setDate(to.getDate() - diffToMonday);
        setRange(from, to);
      },
    },
    {
      label: t("filters.quick_ranges.this_month") as string,
      action: () => {
        const to = new Date();
        const from = new Date(to.getFullYear(), to.getMonth(), 1);
        setRange(from, to);
      },
    },
    {
      label: t("filters.quick_ranges.last_month") as string,
      action: () => {
        const to = new Date();
        const from = new Date(to.getFullYear(), to.getMonth() - 1, 1);
        const end = new Date(to.getFullYear(), to.getMonth(), 0);
        setRange(from, end);
      },
    },
  ];

  return (
    <div className={styles.filters_wrapper}>
      <h2 className={styles.title}>{t("filters.title") as string}</h2>
      <div className={styles.quick_filters}>
        {quickRanges.map((q) => (
          <Button
            key={q.label}
            className={styles.quick_button}
            onClick={q.action}
          >
            {q.label}
          </Button>
        ))}
      </div>
      <div className={styles.filters}>
        <Input
          label={t("filters.search_store") as string}
          placeholder={t("filters.placeholder_search") as string}
          value={storeSearch}
          onChange={(e) => setStoreSearch(e.target.value)}
        />
        <Select
          label={t("filters.store_filter") as string}
          options={
            stores?.map((store) => ({
              id: store.id?.toString(),
              name: store.name,
            })) || []
          }
          loaderRef={loaderRef}
          onChange={(val) => onChangeStore(val.id, val.name)}
          value={storeId}
        />
        <DatePicker
          label={t("filters.date_from") as string}
          value={startDate}
          onChange={onChangeStartDate}
        />
        <DatePicker
          label={t("filters.date_to") as string}
          value={endDate}
          onChange={onChangeEndDate}
        />
        <div className={styles.btns}>
          <Button onClick={onApply}>
            {t("filters.apply_button") as string}
          </Button>
          <Button className={styles.reset} onClick={onReset}>
            {t("common.reset") as string}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Filters;
